import React, { useEffect, useMemo, useState } from "react";
import { useRoom } from "./RoomProvider";
import { offerRideId, offerAgeSeconds } from "../../hooks/useKaizenLiveOffers";
import { useReturnRideQueue } from "../../hooks/useReturnRideQueue";
import { ZoneBadge, SameZoneToggle } from "../../components/ZoneBadge";

/**
 * IncomingOffersLane — the VA-only arrivals dock on The Floor.
 *
 * Presentation layer only. Every byte of data comes through the shared
 * `useKaizenLiveOffers` hook (via RoomProvider). No duplicate fetches.
 * Multi-state: a market filter pill scopes the list to one state (IL/NC/SC/GA)
 * or shows all. The filter is client-side — no extra fetches.
 */

// Detect the state code from a Kaizen offer doc.
// Prefers the stored `pickup_state` field, falls back to parsing the address.
function offerState(offer) {
  if (offer.pickup_state) return offer.pickup_state.toUpperCase();
  const addr = offer?.offer?.pickup_address || offer?.pickup_address || "";
  const m = addr.match(/,\s*([A-Z]{2})\s+\d{5}/);
  return m ? m[1] : null;
}

// All state codes currently active across the offer list.
function activeStates(offers) {
  const seen = new Set();
  offers.forEach(o => { const s = offerState(o); if (s) seen.add(s); });
  return [...seen].sort();
}

export default function IncomingOffersLane() {
  const {
    incomingOffers,
    offerBusy,
    offerSuggestions,
    loadOfferSuggestions,
    acceptOffer,
    declineOffer,
    focusOn,
  } = useRoom();

  const returnRides = useReturnRideQueue({ windowHours: 4 });

  const [collapsed, setCollapsed] = useState(true);
  const [autoAssign, setAutoAssign] = useState(false);
  const [confirming, setConfirming] = useState(null);
  // Multi-state market filter — null means "ALL"
  const [marketFilter, setMarketFilter] = useState(null);

  const allOffers = useMemo(() => {
    const list = Array.isArray(incomingOffers) ? incomingOffers : [];
    return [...list].sort((a, b) => {
      const ta = new Date(a.last_seen_at || a.ingested_at || 0).getTime();
      const tb = new Date(b.last_seen_at || b.ingested_at || 0).getTime();
      return tb - ta;
    });
  }, [incomingOffers]);

  const markets = useMemo(() => activeStates(allOffers), [allOffers]);

  const offers = useMemo(
    () => marketFilter ? allOffers.filter(o => offerState(o) === marketFilter) : allOffers,
    [allOffers, marketFilter]
  );

  return (
    <aside
      className={`incoming-lane ${collapsed ? "incoming-lane--collapsed" : ""}`}
      data-testid="incoming-offers-lane"
    >
      <header className="il-head">
        <div className="il-title">
          <span className="il-dot" />
          <span className="il-label">KAIZEN · INCOMING</span>
          <span className="il-count" data-testid="incoming-offers-count">
            {offers.length}{marketFilter ? `/${allOffers.length}` : ""}
          </span>
        </div>
        <button
          className="il-collapse"
          onClick={() => setCollapsed(c => !c)}
          data-testid="incoming-offers-toggle"
        >
          {collapsed ? "expand" : "collapse"}
        </button>
      </header>

      {!collapsed && (
        <>
          {/* Multi-state market filter — only shown when >1 state active */}
          {markets.length > 1 && (
            <div className="il-market-filter" data-testid="il-market-filter">
              {[null, ...markets].map(m => (
                <button
                  key={m || "ALL"}
                  onClick={() => setMarketFilter(m)}
                  data-testid={`il-market-${m || "all"}`}
                  className={`il-market-pill ${marketFilter === m ? "il-market-pill--active" : ""}`}
                >
                  {m || "ALL"}
                </button>
              ))}
            </div>
          )}

          <div className="il-mode">
            <label className="il-mode-toggle">
              <input
                type="checkbox"
                checked={autoAssign}
                onChange={(e) => setAutoAssign(e.target.checked)}
                data-testid="incoming-auto-assign-toggle"
              />
              <span>auto-assign on accept</span>
            </label>
            <span className="il-mode-hint">
              {autoAssign ? "trip lands ACCEPTED (auto-assigner picks)" : "trip lands PENDING (VA picks TP/driver)"}
            </span>
          </div>

          <div className="il-list">
            {offers.length === 0 ? (
              <div className="il-empty" data-testid="incoming-offers-empty">
                <div className="il-empty-line">the broker feed is quiet</div>
                <div className="il-empty-sub">nova is watching · 5s cadence</div>
              </div>
            ) : (
              offers.map((o) => {
                const rid = offerRideId(o);
                return (
                  <OfferCard
                    key={rid || o.kaizen_offer_id}
                    offer={o}
                    rideId={rid}
                    busy={offerBusy[rid]}
                    ranked={offerSuggestions[rid]}
                    loadRanked={() => loadOfferSuggestions(rid)}
                    onAccept={() => setConfirming({ rideId: rid, action: "accept", offer: o })}
                    onDecline={() => setConfirming({ rideId: rid, action: "decline", offer: o })}
                    onFocus={() => focusOn && focusOn("offer", rid)}
                  />
                );
              })
            )}

            {/* ─── Return-ride proactive queue ─── */}
            {returnRides.length > 0 && (
              <div className="il-section" data-testid="return-rides-section">
                <div className="il-section-head">
                  <span className="il-section-dot" />
                  <span className="il-section-label">RETURN RIDES · NOVA PRE-VERIFIED</span>
                  <span className="il-count">{returnRides.length}</span>
                </div>
                {returnRides.map((r) => (
                  <ReturnRideCard key={r.trip.trip_id} entry={r} onFocus={() => focusOn && focusOn("realTrip", r.trip.trip_id)} />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {confirming && (
        <ConfirmDialog
          detail={confirming}
          onCancel={() => setConfirming(null)}
          onConfirm={async () => {
            const { rideId, action } = confirming;
            setConfirming(null);
            if (action === "accept") await acceptOffer(rideId, { autoAssign });
            else await declineOffer(rideId);
          }}
        />
      )}
    </aside>
  );
}

function OfferCard({ offer, rideId, busy, ranked, loadRanked, onAccept, onDecline, onFocus }) {
  const [expanded, setExpanded] = useState(false);
  const [sameZoneOnly, setSameZoneOnly] = useState(false);
  const off = offer.offer || {};
  const age = offerAgeSeconds(offer);
  const pickup = off.pickup_address || "pickup pending";
  const dropoff = off.dropoff_address || "dropoff pending";
  const mobility = (off.mobility_type || "amb").toString().toUpperCase();
  const apptDate = off.pickup_date;
  const apptTime = off.pickup_time;
  const miles = off.distance_miles || off.miles;
  const patient = off.patient_name;
  // Provenance — kaizen_email_trigger.py stamps "email_trigger" on the fast
  // IMAP-parsed path (~1.2s from the broker's email hitting the portal URL
  // directly); the 5-min scheduled portal scan stamps plain "kaizen". Same
  // field exists in Live Ops' KaizenPanel data but was never surfaced there
  // either — this is a genuinely new visual, not a re-skin of an existing one.
  const viaEmail = offer.source === "email_trigger";
  const urgentLead = off.urgent_lead_time === true;
  const leadHoursLabel = off.lead_time_hours != null ? `${off.lead_time_hours.toFixed(1)}h notice` : "<24h notice";

  // Pre-verify (Nova ranker) once the card is expanded — no need to burn a
  // ranking call for rows the VA hasn't even opened yet.
  useEffect(() => {
    if (!rideId || !expanded) return;
    if (ranked === undefined) loadRanked();
  }, [rideId, expanded, ranked, loadRanked]);

  const top = Array.isArray(ranked) && ranked.length > 0 ? ranked[0] : null;
  const cardBandClass = top?.match_band === "green" ? "il-card--green"
    : top?.match_band === "yellow" ? "il-card--yellow"
    : ranked && ranked.length > 0 ? "il-card--red"
    : "";

  const isBusy = !!busy;

  return (
    <article
      className={`il-card ${cardBandClass} ${isBusy ? "il-card--busy" : ""} ${expanded ? "il-card--expanded" : "il-card--collapsed"}`}
      data-testid={`offer-card-${rideId}`}
    >
      <div className="il-row-compact" onClick={() => setExpanded((e) => !e)} data-testid={`offer-row-toggle-${rideId}`}>
        <span className={`il-row-chevron ${expanded ? "il-row-chevron--open" : ""}`}>▸</span>
        <span className="il-mob-tag">{mobility}</span>
        <span className="il-rid">{rideId}</span>
        <span className="il-row-name">{patient || "unnamed rider"}</span>
        <span className="il-row-when">{apptDate || apptTime ? `${apptDate || ""} ${apptTime || ""}`.trim() : "—"}</span>
        {offerState(offer) && offerState(offer) !== "IL" && (
          <span className="il-state-badge" data-testid={`offer-state-${rideId}`}>{offerState(offer)}</span>
        )}
        {age != null && (
          <span className={`il-age ${age > 30 ? "il-age--stale" : ""}`}>
            {age < 60 ? `${age}s ago` : `${Math.round(age / 60)}m ago`}
          </span>
        )}
      </div>

      {expanded && (
        <div className="il-row-detail" data-testid={`offer-detail-${rideId}`}>
      <div className="il-card-top">
        <div className="il-card-id">
          {viaEmail && (
            <span className="il-provenance" data-testid={`offer-provenance-${rideId}`} title="Fast-tracked straight from the broker's email — 1.2s from inbox to this card, no waiting on the 5-min portal scan">
              ⚡ parsed from broker email
            </span>
          )}
          {urgentLead && (
            <span className="il-urgent" data-testid={`offer-urgent-${rideId}`} title="Pickup is less than 24 hours away — needs manual handling, not the normal TP offer flow">
              ⏱ urgent · {leadHoursLabel}
            </span>
          )}
          {["no_answer", "declined", "call_failed", "held_cooldown"].includes(offer.voice_auth_status) && (
            <span
              className="il-urgent"
              data-testid={`offer-voice-held-${rideId}`}
              title={`Voice-auth call went unanswered ${offer.voice_auth_attempts || 1}× — accept/decline below to unblock it manually`}
            >
              ☎ no phone answer ({offer.voice_auth_attempts || 1}×)
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {miles && <span className="il-miles">{Number(miles).toFixed(1)} mi</span>}
          <button
            className="il-row-focus-btn"
            onClick={(e) => { e.stopPropagation(); onFocus(); }}
            data-testid={`offer-focus-${rideId}`}
          >
            focus on map
          </button>
        </div>
      </div>

      <div className="il-card-route">
        <div className="il-route-line">
          <span className="il-pt il-pt--pu">PU</span>
          <span className="il-addr">{shorten(pickup)}</span>
        </div>
        <div className="il-route-line">
          <span className="il-pt il-pt--do">DO</span>
          <span className="il-addr">{shorten(dropoff)}</span>
        </div>
      </div>

      <div className="il-nova">
        <div className="il-nova-head">
          <span className="il-nova-dot" />
          <span className="il-nova-label">nova · pre-verified shortlist</span>
          {Array.isArray(ranked) && ranked.length > 0 && (
            <SameZoneToggle checked={sameZoneOnly} onChange={setSameZoneOnly} testId={`offer-same-zone-${rideId}`} style={{ marginLeft: "auto" }} />
          )}
        </div>
        {ranked === undefined && <div className="il-nova-loading">ranking providers…</div>}
        {Array.isArray(ranked) && ranked.length === 0 && (
          <div className="il-nova-loading il-nova-err">no matches in service area · manual review</div>
        )}
        {Array.isArray(ranked) && ranked.length > 0 && (
          <ul className="il-rank">
            {(sameZoneOnly ? ranked.filter((r) => r.same_zone) : ranked).slice(0, 3).map((r, i) => (
              <li
                key={`${r.kind}-${r.id}-${i}`}
                className={`il-rank-row il-rank-row--${r.match_band || "unk"}`}
                data-testid={`offer-rank-${rideId}-${i}`}
              >
                <span className="il-rank-idx">{i + 1}</span>
                <span className="il-rank-name">{r.name || `${r.kind} ${String(r.id).slice(0, 6)}`}</span>
                <span className="il-rank-meta">
                  {r.distance_mi != null ? `${Number(r.distance_mi).toFixed(1)}mi` : "—"}
                  <ZoneBadge zoneName={r.zone_name} sameZone={r.same_zone} />
                  {r.match_band === "green" && <span className="il-band il-band--g">in-area</span>}
                  {r.match_band === "yellow" && <span className="il-band il-band--y">edge</span>}
                  {r.match_band === "red" && <span className="il-band il-band--r">out</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="il-actions">
        <button
          className="il-btn il-btn--accept"
          disabled={isBusy}
          onClick={(e) => { e.stopPropagation(); onAccept(); }}
          data-testid={`offer-accept-${rideId}`}
        >
          {busy === "accept" ? "accepting on kaizen…" : "accept"}
        </button>
        <button
          className="il-btn il-btn--decline"
          disabled={isBusy}
          onClick={(e) => { e.stopPropagation(); onDecline(); }}
          data-testid={`offer-decline-${rideId}`}
        >
          {busy === "decline" ? "declining…" : "decline"}
        </button>
      </div>
        </div>
      )}
    </article>
  );
}

function ConfirmDialog({ detail, onCancel, onConfirm }) {
  const off = detail.offer?.offer || {};
  const isAccept = detail.action === "accept";
  return (
    <div className="il-confirm" role="dialog" data-testid="offer-confirm-dialog">
      <div className="il-confirm-card">
        <div className="il-confirm-title">
          {isAccept ? "accept on kaizen?" : "decline on kaizen?"}
        </div>
        <div className="il-confirm-body">
          MedexOVA will drive the headless browser to click <b>{isAccept ? "Accept" : "Decline"}</b> on Kaizen for this ride.
        </div>
        <div className="il-confirm-detail">
          <div><span>ride</span> {detail.rideId}</div>
          <div><span>patient</span> {off.patient_name || "—"}</div>
          <div><span>when</span> {off.pickup_date || ""} {off.pickup_time || ""}</div>
          <div><span>from</span> {off.pickup_address || "?"}</div>
          <div><span>to</span> {off.dropoff_address || "?"}</div>
        </div>
        <div className="il-confirm-actions">
          <button className="il-btn il-btn--decline" onClick={onCancel} data-testid="offer-confirm-cancel">cancel</button>
          <button
            className={`il-btn ${isAccept ? "il-btn--accept" : "il-btn--danger"}`}
            onClick={onConfirm}
            data-testid="offer-confirm-proceed"
          >
            {isAccept ? "yes, accept" : "yes, decline"}
          </button>
        </div>
      </div>
    </div>
  );
}

function shorten(addr) {
  if (!addr) return "";
  const parts = String(addr).split(",");
  if (parts.length <= 2) return addr;
  return `${parts[0].trim()}, ${parts[1].trim()}`;
}

function ReturnRideCard({ entry, onFocus }) {
  const t = entry.trip;
  const rider = t.rider?.name || "rider";
  const expected = entry.expected_at ? new Date(entry.expected_at) : null;
  const now = Date.now();
  const secondsUntil = expected ? Math.max(0, Math.round((expected.getTime() - now) / 1000)) : null;
  const label = secondsUntil == null ? ""
    : secondsUntil < 60 ? `in ${secondsUntil}s`
    : secondsUntil < 3600 ? `in ${Math.round(secondsUntil / 60)}m`
    : `in ${Math.floor(secondsUntil / 3600)}h ${Math.round((secondsUntil % 3600) / 60)}m`;
  return (
    <article className="il-card il-card--return" data-testid={`return-ride-${t.trip_id}`} onClick={onFocus}>
      <div className="il-card-top">
        <div className="il-card-id">
          <span className="il-mob-tag il-mob-tag--return">RETURN</span>
          <span className="il-rid">{rider}</span>
        </div>
        <span className="il-age">{label}</span>
      </div>
      <div className="il-card-route">
        <div className="il-route-line">
          <span className="il-pt il-pt--pu">PU</span>
          <span className="il-addr">{shorten(t.dropoff?.address || "")}</span>
        </div>
        <div className="il-route-line">
          <span className="il-pt il-pt--do">DO</span>
          <span className="il-addr">{shorten(t.pickup?.address || "")}</span>
        </div>
      </div>
      <div className="il-nova">
        <div className="il-nova-head">
          <span className="il-nova-dot" />
          <span className="il-nova-label">nova · {entry.kind === "explicit" ? "scheduled return" : "predicted return · confirm to schedule"}</span>
        </div>
      </div>
      <div className="il-actions">
        <button className="il-btn il-btn--accept" data-testid={`return-ride-approve-${t.trip_id}`}>
          approve · pre-verify driver
        </button>
      </div>
    </article>
  );
}
